import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

const API_URL = '/api/assets135/assets';

// Real-world approximate market caps in Trillions (USD) for visual scale, 
// since the current endpoint returns rank in the market_cap field.
// We will merge this with the live ranking data from your API.
const ASSET_CLASSES = {
  'Gold': { capT: 15.5, color: '#FFD700', type: 'Metal' },
  'Microsoft': { capT: 3.1, color: '#3b82f6', type: 'Tech' },
  'Apple': { capT: 3.0, color: '#3b82f6', type: 'Tech' },
  'NVIDIA': { capT: 2.9, color: '#3b82f6', type: 'Tech' },
  'Saudi Aramco': { capT: 2.0, color: '#ea580c', type: 'Energy' },
  'Silver': { capT: 1.8, color: '#e5e7eb', type: 'Metal' },
  'Bitcoin': { capT: 1.4, color: '#F7931A', type: 'Crypto' },
  'Meta Platforms (Facebook)': { capT: 1.2, color: '#3b82f6', type: 'Tech' },
  'Berkshire Hathaway': { capT: 0.9, color: '#8b5cf6', type: 'Finance' },
  'Ethereum': { capT: 0.4, color: '#627EEA', type: 'Crypto' }
};

export const BitcoinGlobalScaleCard = ({ theme }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await fetch(API_URL);
        const rawData = await response.json();

        // Filter the API payload for our specific global benchmarks
        // and map the real-world scale alongside the live ranking.
        const scaleData = rawData
          .filter(item => ASSET_CLASSES[item.name])
          .map(item => {
            const assetInfo = ASSET_CLASSES[item.name];
            return {
              name: item.name,
              rank: parseInt(item.market_cap, 10), // Your API puts rank here
              cap: assetInfo.capT,
              color: assetInfo.color,
              type: assetInfo.type
            };
          })
          .sort((a, b) => b.cap - a.cap); // Sort highest market cap to lowest

        setData(scaleData);
      } catch (err) {
        console.error('Error fetching global assets:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
    // Optional: Refresh interval if rankings shift often
    const interval = setInterval(fetchAssets, 60000); 
    return () => clearInterval(interval);
  }, []);

  // Custom tooltip to show the formatted Trillion USD value and Live Rank
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          background: theme.cardBg,
          border: `1px solid ${theme.border}`,
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}>
          <p style={{ color: theme.textPrimary, fontWeight: 'bold', margin: '0 0 4px 0' }}>
            {data.name}
          </p>
          <p style={{ color: data.color, margin: '0 0 4px 0', fontSize: '1.1rem', fontWeight: '800' }}>
            ~${data.cap.toFixed(2)} Trillion
          </p>
          <p style={{ color: theme.textSecondary, margin: 0, fontSize: '0.8rem' }}>
            Global Rank: #{data.rank}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div style={{
        background: theme.cardBg, borderRadius: '16px', padding: '1.5rem',
        border: `1px solid ${theme.border}`, minHeight: '400px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary
      }}>
        Mapeando escala global...
      </div>
    );
  }

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      width: '100%'
    }}>
      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
        <div>
          <div style={{
            fontSize: '0.75rem', color: theme.textSecondary, textTransform: 'uppercase',
            fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            🌍 Escala Macro (Top Assets)
            <span style={{ background: 'rgba(247, 147, 26, 0.1)', color: '#F7931A', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>
              BTC RELATIVE SIZE
            </span>
          </div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: theme.textPrimary, margin: 0 }}>
            Market Cap Global vs Bitcoin
          </h3>
          <p style={{ fontSize: '0.85rem', color: theme.textSecondary, margin: '0.25rem 0 0 0' }}>
            Comparativo de tamanho (Em Trilhões USD)
          </p>
        </div>
      </div>

      {/* The Horizontal Bar Chart */}
      <div style={{ height: '400px', width: '100%', marginTop: '1rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 0, right: 40, left: 10, bottom: 0 }}
          >
            <XAxis 
              type="number" 
              hide={true} // Hide X axis to keep it clean
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              axisLine={false}
              tickLine={false}
              width={140}
              tick={{ fill: theme.textSecondary, fontSize: 12, fontWeight: 600 }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
            
            <Bar 
              dataKey="cap" 
              radius={[0, 4, 4, 0]} 
              barSize={20}
              animationDuration={1500}
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color} 
                  opacity={entry.name === 'Bitcoin' ? 1 : 0.75} // Highlight BTC
                />
              ))}
              <LabelList 
                dataKey="cap" 
                position="right" 
                formatter={(val) => `$${val}T`} 
                style={{ fill: theme.textPrimary, fontSize: '11px', fontWeight: 'bold' }} 
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BitcoinGlobalScaleCard;